import { FC, useState } from 'react';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import { RenderIf } from 'shared/helpers/renderIf';
import { ConfirmationModal } from 'entities/ConfirmationModal';
import { useTranslations } from 'shared/hooks/useTranslations';
import { useCloseModalWithDelay } from 'shared/helpers/useCloseModalWithDelay';
import { useStore } from 'effector-react';
import { settingsStore } from 'shared/store';
import { Button } from 'entities/Button';
import { defaultPrint } from 'shared/helpers/printFunction/sunmiPrint';
import { getLayout } from 'shared/helpers/getLayout';
import { handleLocalStorage } from 'shared/helpers/handleLocalStorage';
import { PrinterDataType } from 'shared/store/settingsStore/types';
import { errorHandler } from 'shared/helpers/errorHandler';

export const PrintButton: FC<IPrintButton> = ({ dataToPrint }) => {
  //
  // Hooks
  const t = useTranslations();
  const [progress, startClosing] = useCloseModalWithDelay(() => setIsPrintModalOpen(false), 3500);

  // State
  const { isPrinterDataInit } = useStore(settingsStore);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const printersList = useStore(settingsStore).printersList;

  // Functions
  const getPrinter = (): PrinterDataType | undefined => {
    const shippingPoint = handleLocalStorage('shippingPointName');
    return printersList?.find((printer) => printer.shippingPoint === shippingPoint);
  };

  const startPrintingProduct = async () => {
    const printer = getPrinter();
    const isLocalConnection = printer?.isLocalConnection;
    const printerId = printer?.printerId;
    startClosing();
    if (!isLocalConnection && printerId) {
      // Awaited and caught so a rejected print mutation surfaces as a toast instead of
      // an unhandled rejection that only ever showed up in Sentry (WA-3601).
      try {
        await defaultPrint({ printerId, printData: dataToPrint });
      } catch (e) {
        errorHandler(e, t('errors.error'));
      }
    }
  };

  return (
    <>
      <RenderIf
        condition={isPrinterDataInit && Boolean(dataToPrint) && Boolean(getPrinter()?.isOnline)}
      >
        <Button
          color="warning"
          variant={getLayout() === 'DESKTOP' ? 'outlined' : 'text'}
          height="56px"
          width="56px"
          handleClick={() => {
            setIsPrintModalOpen(true);
          }}
        >
          <LocalPrintshopOutlinedIcon />
        </Button>
      </RenderIf>

      <ConfirmationModal
        isOpen={isPrintModalOpen}
        progress={progress}
        headerTitle={t('production.print')}
        text={t('production.printQuestion')}
        onClose={() => setIsPrintModalOpen(false)}
        onConfirm={startPrintingProduct}
      />
    </>
  );
};

interface IPrintButton {
  dataToPrint: any;
}
